import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  TextField,
  Button,
  Select,
  MenuItem,
  OutlinedInput,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Rocket,
  Target,
  MapPin,
  Calendar,
  Tags,
  DollarSign,
  ImagePlus,
  MessageSquare,
  Type,
  Trash2,
  PlusCircle,
  ChevronDown,
  Check,
  Navigation,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  AlertOctagon,
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Navbar from "../../../components/Navbar";
import {
  regionOptions,
  locationsByRegion,
  categoryOptions,
} from "../../../utils/data";
import { toast } from "react-toastify";
import { AnimatePresence } from "framer-motion";
import { DealsRequest } from "../../../requests/vendor/DealsRequest";
import { campaignsRequest } from "../../../requests/vendor/CampaignsRequest";
import Loader from "../../../components/Loader";

const PriceWarning = ({ message }) => (
  <motion.div
    initial={{ opacity: 0, y: -5, height: 0 }}
    animate={{ opacity: 1, y: 0, height: "auto" }}
    exit={{ opacity: 0, y: -5, height: 0 }}
    transition={{
      type: "spring",
      stiffness: 400,
      damping: 30,
    }}
    className="overflow-hidden mt-2"
  >
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex gap-2 items-center p-2 pr-3 bg-red-50 rounded-lg border border-red-100"
    >
      <motion.div
        animate={{
          scale: [1, 1.05, 1],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="p-1 bg-red-100 rounded-md"
      >
        <AlertTriangle className="w-4 h-4 text-red-500" />
      </motion.div>
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="text-sm font-medium text-red-600"
      >
        {message}
      </motion.span>
    </motion.div>
  </motion.div>
);

const CreateCampaignDeal = () => {
  const navigate = useNavigate();
  const [imagePreview, setImagePreview] = useState(null);
  const [categories, setCategories] = useState([]);
  const [redemptionType, setRedemptionType] = useState([]);
  const INITIAL_ORIGINAL_PRICE = "";
  const INITIAL_DISCOUNT_PRICE = "";
  const [originalPrice, setOriginalPrice] = useState(INITIAL_ORIGINAL_PRICE);
  const [discountPrice, setDiscountPrice] = useState(INITIAL_DISCOUNT_PRICE);
  const [phoneNumber, setPhoneNumber] = useState("233");
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const [startDate, setStartDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [endDate, setEndDate] = useState("");
  const [smsContent, setSmsContent] = useState("");
  const MAX_CHARS = 90;
  const [selectAllLocations, setSelectAllLocations] = useState(false);
  const [regionLocations, setRegionLocations] = useState([
    {
      region: "",
      location: "",
      customLocation: "",
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [dealSlug, setDealSlug] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    category: [],
    companyName: "",
    location: [],
    expiry_date: "",
    start_date: "",
    redemption_type: [],
    redemption_instructions: "",
    refund_policy: "",
    phone_number: "",
    price: "",
    discounted_price: "",
    terms_and_conditions: "",
    region_location: [
      {
        region: "",
        location: "",
      },
    ],
    description: "",
  });
  const { slug } = useParams();

  const [charCounts, setCharCounts] = useState({
    redemption_instructions: 0,
    refund_policy: 0,
    terms_and_conditions: 0,
  });

  const MAX_POLICY_CHARS = 250;
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (slug) {
      const fetchDealDetails = async () => {
        try {
          setIsLoading(true);
          const deal = await DealsRequest.getDealBySlug(slug);
          setDealSlug(deal?.data?.deal_slug);
          setSelectedData(deal?.data);

          const formattedStartDate =
            deal.data.start_date?.split(" ")[0] ||
            new Date().toISOString().split("T")[0];
          const formattedExpiryDate = deal.data.expiry_date?.split(" ")[0] || "";

          // Directly set the image URL without converting to base64
          if (deal.data.image) {
            setImagePreview(deal.data.image);
          }

          // Populate all form fields
          setCategories(deal.data.category || []);
          setRedemptionType(deal.data.redemption_type || []);
          setPhoneNumber(deal.data.phone_number || "");
          setStartDate(formattedStartDate);
          setEndDate(formattedExpiryDate);
          setOriginalPrice(deal.data.price || "");
          setDiscountPrice(deal.data.discounted_price || "");

          // Handle region locations - check both location and region_location fields
          const locationData = deal.data.region_location || deal.data.location;
          console.log("locationData", locationData);
          if (locationData?.length > 0) {
            const firstLocation = locationData[0];
            console.log("firstLocation", firstLocation);
            // Check if both region and location are "all"
            if (firstLocation.region === "all" && firstLocation.location === "all") {
              setSelectAllLocations(true);
              setRegionLocations([{ region: "all", location: "all" }]);
            } else {
              setSelectAllLocations(false);
              const processedLocations = locationData.map((loc) => {
                const regionLocations = locationsByRegion[loc.region] || [];
                const isCustomLocation = !regionLocations.includes(loc.location);
                
                return {
                  region: loc.region || "",
                  location: isCustomLocation ? "other" : loc.location,
                  customLocation: isCustomLocation ? loc.location : "",
                };
              });
              setRegionLocations(processedLocations);
            }
          }
          setCharCounts({
            redemption_instructions: (deal.data.redemption_instructions || "")
              .length,
            refund_policy: (deal.data.refund_policy || "").length,
            terms_and_conditions: (deal.data.terms_and_conditions || "").length,
          });

          // Update form data
          setFormData({
            ...deal.data,
            title: deal.data.title || "",
            category: deal.data.category || [],
            companyName: deal.data.companyName || "",
            location: locationData || [],
            expiry_date: formattedExpiryDate,
            start_date: formattedStartDate,
            redemption_type: deal.data.redemption_type || [],
            redemption_instructions: deal.data.redemption_instructions || "",
            refund_policy: deal.data.refund_policy || "",
            phone_number: deal.data.phone_number || "",
            price: deal.data.price || "",
            discounted_price: deal.data.discounted_price || "",
            terms_and_conditions: deal.data.terms_and_conditions || "",
            region_location: locationData || [{ region: "", location: "" }],
            description: deal.data.description || "",
          });
        } catch (error) {
          toast.error("Failed to fetch deal details");
        } finally {
          setIsLoading(false);
        }
      };
      fetchDealDetails();
    } else {
      // Set initial start date for new campaigns
      const today = new Date().toISOString().split("T")[0];
      setStartDate(today);
      setFormData((prev) => ({
        ...prev,
        start_date: today,
      }));
    }
  }, [slug]);

  useEffect(() => {
    if (slug) {
      setDealSlug(slug);
    }
  }, [slug]);

  const convertImageToBase64 = async (imageUrl) => {
    try {
      // Check if the URL is from the backend storage
      if (imageUrl.includes('storage/uploads')) {
        // Add the necessary CORS headers for backend requests
        const response = await fetch(imageUrl, {
          credentials: 'include',
          headers: {
            'Accept': 'image/*',
            'Origin': window.location.origin,
          },
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error('Failed to fetch image');
        }

        const blob = await response.blob();
        
        if (!blob.type.startsWith('image/')) {
          throw new Error('Response is not an image');
        }

        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } else {
        // For external URLs, create a new image element to handle CORS
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/jpeg'));
          };
          img.onerror = () => reject(new Error('Failed to load image'));
          img.src = imageUrl;
        });
      }
    } catch (error) {
      console.error('Error converting image to base64:', error);
      return null;
    }
  };

  const redemptionOptions = ["In-Store", "Online"];

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const calculateDiscount = () => {
    if (!originalPrice || !discountPrice) return null;
    const discount = ((originalPrice - discountPrice) / originalPrice) * 100;
    return Math.round(discount * 100) / 100;
  };

  const handleScroll = (e) => {
    const element = e.target;
    const isAtBottom =
      element.scrollHeight - element.scrollTop === element.clientHeight;
    setShowScrollIndicator(!isAtBottom);
  };

  const calculateDays = () => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const charCount = smsContent.length;
  const charRemaining = MAX_CHARS - charCount;

  const handleOriginalPriceChange = (e) => {
    const newOriginalPrice = parseFloat(e.target.value);
    setOriginalPrice(e.target.value);

    if (discountPrice && parseFloat(discountPrice) > newOriginalPrice) {
      setDiscountPrice(e.target.value);
    }
  };

  const handleDiscountPriceChange = (e) => {
    const newValue = e.target.value;

    if (newValue === "") {
      setDiscountPrice("");
      return;
    }

    const newDiscountPrice = parseFloat(newValue);
    const originalPriceNum = parseFloat(originalPrice);
    if (
      !isNaN(newDiscountPrice) &&
      !isNaN(originalPriceNum) &&
      newDiscountPrice <= originalPriceNum
    ) {
      setDiscountPrice(newValue);
    }
  };

  //* Add region location
  const addRegionLocation = () => {
    if (regionLocations.length >= 5) {
      toast.error("Maximum of 5 locations allowed");
      return;
    }
    setRegionLocations([
      ...regionLocations,
      { region: "", location: "", customLocation: "" },
    ]);
  };

  const removeRegionLocation = (index) => {
    const newRegionLocations = regionLocations.filter((_, i) => i !== index);
    setRegionLocations(newRegionLocations);
  };

  const handleResetPricing = () => {
    setOriginalPrice(INITIAL_ORIGINAL_PRICE);
    setDiscountPrice(INITIAL_DISCOUNT_PRICE);
  };

  const handleCategoryChange = (event) => {
    const value = event.target.value;
    setCategories(value);
    setFormData((prev) => ({ ...prev, category: value }));
  };

  const handleRedemptionTypeChange = (event) => {
    const value = event.target.value;
    setRedemptionType(value);
    setFormData((prev) => ({ ...prev, redemption_type: value }));
  };

  const handlePhoneNumberChange = (event) => {
    let value = event.target.value.replace(/\D/g, "");

    // Ensure the number starts with 233
    if (!value.startsWith("233")) {
      value = "233" + value;
    }

    // Limit to 12 digits total
    if (value.length <= 12) {
      setPhoneNumber(value);
      setFormData((prev) => ({ ...prev, phone_number: value }));
    }
  };

  //* Handle date change
  const handleDateChange = (field, value) => {
    if (field === "start_date") {
      setStartDate(value);
      setFormData((prev) => ({
        ...prev,
        start_date: value,
        expiry_date: prev.expiry_date,
      }));
    } else {
      setEndDate(value);
      setFormData((prev) => ({
        ...prev,
        expiry_date: value,
        start_date: prev.start_date,
      }));
    }
  };

  //* Handle price change
  const handlePriceChange = (field, value) => {
    if (field === "price") {
      setOriginalPrice(value);
    } else {
      setDiscountPrice(value);
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  //* Handle region location change
  const handleRegionLocationChange = (index, field, value) => {
    const newRegionLocations = [...regionLocations];
    newRegionLocations[index] = {
      ...newRegionLocations[index],
      [field]: value,
    };
    setRegionLocations(newRegionLocations);

    const formDataLocations = selectAllLocations 
      ? [{ region: "all", location: "all" }]
      : newRegionLocations.map(loc => ({
          region: loc.region,
          location: loc.location === 'other' ? loc.customLocation : loc.location
        }));

    setFormData(prev => ({
      ...prev,
      region_location: formDataLocations
    }));
  };

  //* Handle text field change
  const handleTextFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Update character counts for specific fields
    if (
      [
        "redemption_instructions",
        "refund_policy",
        "terms_and_conditions",
      ].includes(field)
    ) {
      setCharCounts((prev) => ({
        ...prev,
        [field]: value.length,
      }));
    }
  };

  //* Submit campaign
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      let finalImageData = imagePreview;

      if (imagePreview && imagePreview.startsWith('http')) {
        try {
          const base64Image = await convertImageToBase64(imagePreview);
          if (base64Image) {
            finalImageData = base64Image;
          }
        } catch (error) {
          console.error('Error converting image:', error);
        }
      }

      const completeFormData = {
        ...formData,
        location: selectAllLocations
          ? [{location:"all", region:"all"}]
          : formData.region_location.map(loc => ({
              region: loc.region,
              location: loc.location === 'other' ? loc.customLocation : loc.location
            })),
        sms_content: smsContent,
        price: Number(originalPrice).toFixed(2),
        discounted_price: Number(discountPrice).toFixed(2),
      };

      const response = await campaignsRequest.createOrUpdateCampaign(
        completeFormData,
        finalImageData,
        dealSlug
      );

      if (response.success) {
        toast.success(response.message);
        navigate("/deal/campaigns");
      } else {
        if (response.details?.errors) {
          Object.entries(response.details.errors).forEach(
            ([field, messages]) => {
              toast.error(`${field}: ${messages[0]}`);
            }
          );
        } else {
          toast.error(response.error);
        }
      }
    } catch (error) {
      toast.error("Failed to submit campaign");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Also add this handler for custom location changes
  const handleCustomLocationChange = (index, value) => {
    const newRegionLocations = [...regionLocations];
    newRegionLocations[index] = {
      ...newRegionLocations[index],
      customLocation: value
    };
    setRegionLocations(newRegionLocations);

    // Update formData with the correct location value
    const formDataLocations = newRegionLocations.map(loc => ({
      region: loc.region,
      location: loc.location === 'other' ? value : loc.location
    }));

    setFormData(prev => ({
      ...prev,
      region_location: formDataLocations
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <Navbar page="Create Campaign Deal" />

      {isLoading ? (
        <Loader />
      ) : (
        <div className="px-4 py-6 sm:px-6">
          <div className="mx-auto max-w-7xl">
            {/* Main Content */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
              {/* Left Column - Scrollable Forms */}
              <div className="relative lg:col-span-2">
                <div
                  className="space-y-6 max-h-[calc(100vh-180px)] overflow-y-auto pr-6 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent"
                  onScroll={handleScroll}
                >
                  {/* Campaign Details Section */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="p-8 bg-white rounded-2xl border border-gray-100 shadow-sm"
                  >
                    <div className="flex gap-3 items-center mb-6">
                      <div className="p-3 bg-[#FF6C6C]/10 rounded-xl">
                        <Rocket className="w-6 h-6 text-[#FF6C6C]" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                          Campaign Details
                        </h2>
                        <p className="text-sm text-gray-500">
                          Set up your campaign basics
                        </p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <TextField
                        label="Campaign Title"
                        variant="outlined"
                        fullWidth
                        required
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            "&:hover fieldset": { borderColor: "#FF6C6C" },
                            "&.Mui-focused fieldset": {
                              borderColor: "#FF6C6C",
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#FF6C6C",
                          },
                        }}
                        value={formData.title}
                        onChange={(e) =>
                          handleTextFieldChange("title", e.target.value)
                        }
                      />

                      <div className="grid grid-cols-2 gap-4">
                        <FormControl fullWidth>
                          <InputLabel
                            sx={{ "&.Mui-focused": { color: "#FF6C6C" } }}
                          >
                            Categories
                          </InputLabel>
                          <Select
                            multiple
                            value={categories}
                            onChange={handleCategoryChange}
                            input={<OutlinedInput label="Categories" />}
                            sx={{
                              borderRadius: "12px",
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#FF6C6C",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#FF6C6C",
                                },
                            }}
                          >
                            {categoryOptions.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl fullWidth>
                          <InputLabel
                            sx={{ "&.Mui-focused": { color: "#FF6C6C" } }}
                          >
                            Redemption Type
                          </InputLabel>
                          <Select
                            multiple
                            value={redemptionType}
                            onChange={handleRedemptionTypeChange}
                            input={<OutlinedInput label="Redemption Type" />}
                            sx={{
                              borderRadius: "12px",
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#FF6C6C",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#FF6C6C",
                                },
                            }}
                          >
                            {redemptionOptions.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>

                      <TextField
                        label="Contact Phone Number"
                        variant="outlined"
                        fullWidth
                        required
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange}
                        type="tel"
                        placeholder="233XXXXXXXXX"
                        inputProps={{
                          maxLength: 12,
                          minLength: 12,
                        }}
                        helperText="Format: 233XXXXXXXXX (12 digits)"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            "&:hover fieldset": { borderColor: "#FF6C6C" },
                            "&.Mui-focused fieldset": {
                              borderColor: "#FF6C6C",
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#FF6C6C",
                          },
                        }}
                      />

                      <TextField
                        label="Campaign Description"
                        multiline
                        rows={4}
                        variant="outlined"
                        fullWidth
                        value={formData.description || ""}
                        onChange={(e) =>
                          handleTextFieldChange("description", e.target.value)
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            "&:hover fieldset": { borderColor: "#FF6C6C" },
                            "&.Mui-focused fieldset": {
                              borderColor: "#FF6C6C",
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#FF6C6C",
                          },
                        }}
                      />
                    </div>
                  </motion.div>

                  {/* Pricing Section */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm sm:p-6 md:p-8"
                  >
                    <div className="flex flex-col gap-4 justify-between items-start mb-6 sm:flex-row sm:items-center sm:gap-0">
                      <div className="flex gap-3 items-center">
                        <div className="p-3 bg-green-50 rounded-xl">
                          <DollarSign className="w-6 h-6 text-green-500" />
                        </div>
                        <div>
                          <h2 className="text-xl font-semibold text-gray-900">
                            Pricing Details
                          </h2>
                          <p className="text-sm text-gray-500">
                            Set your original and discounted prices
                          </p>
                        </div>
                      </div>

                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleResetPricing}
                        className="flex gap-2 justify-center items-center px-4 py-2 w-full text-sm font-medium text-gray-600 bg-gray-100 rounded-xl transition-all duration-200 hover:bg-gray-200 sm:w-auto"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Reset Pricing
                      </motion.button>
                    </div>

                    <div className="space-y-6">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
                        <TextField
                          label="Original Price (GHC)"
                          type="number"
                          value={originalPrice}
                          onChange={(e) =>
                            handlePriceChange("price", e.target.value)
                          }
                          variant="outlined"
                          fullWidth
                          inputProps={{ min: 0 }}
                          error={Number(originalPrice) <= 0}
                          helperText={
                            Number(originalPrice) <= 0 && (
                              <AnimatePresence mode="wait" initial={false}>
                                <PriceWarning message="Price must be greater than 0" />
                              </AnimatePresence>
                            )
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                              transition: "all 0.2s ease-in-out",
                              "&:hover fieldset": { borderColor: "#FF6C6C" },
                              "&.Mui-focused fieldset": {
                                borderColor: "#FF6C6C",
                              },
                              "&.Mui-error": {
                                animation:
                                  "shake 0.5s cubic-bezier(.36,.07,.19,.97) both",
                                "@keyframes shake": {
                                  "10%, 90%": {
                                    transform: "translate3d(-1px, 0, 0)",
                                  },
                                  "20%, 80%": {
                                    transform: "translate3d(1px, 0, 0)",
                                  },
                                },
                              },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#FF6C6C",
                            },
                            "& .MuiFormHelperText-root": {
                              margin: 0,
                              marginTop: "4px",
                            },
                          }}
                        />
                        <TextField
                          label="Discount Price (GHC)"
                          type="number"
                          value={discountPrice}
                          onChange={(e) =>
                            handlePriceChange(
                              "discounted_price",
                              e.target.value
                            )
                          }
                          variant="outlined"
                          fullWidth
                          disabled={
                            !originalPrice || Number(originalPrice) <= 0
                          }
                          inputProps={{
                            min: 0,
                            max: originalPrice,
                          }}
                          error={Number(discountPrice) > Number(originalPrice)}
                          helperText={
                            !originalPrice ? (
                              <AnimatePresence mode="wait">
                                <PriceWarning message="Set original price first" />
                              </AnimatePresence>
                            ) : Number(discountPrice) >
                              Number(originalPrice) ? (
                              <AnimatePresence mode="wait">
                                <PriceWarning message="Discount price cannot exceed original price" />
                              </AnimatePresence>
                            ) : null
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                              "&:hover fieldset": { borderColor: "#FF6C6C" },
                              "&.Mui-focused fieldset": {
                                borderColor: "#FF6C6C",
                              },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#FF6C6C",
                            },
                            "& .MuiFormHelperText-root": {
                              margin: 0,
                              marginTop: "4px",
                            },
                          }}
                        />
                      </div>

                      {calculateDiscount() !== null && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="p-4 mt-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100"
                        >
                          <div className="flex flex-col gap-4 justify-between items-start sm:flex-row sm:items-center sm:gap-0">
                            <div className="flex gap-3 items-center w-full sm:w-auto">
                              <div className="flex justify-center items-center w-12 h-12 rounded-xl bg-green-500/10 shrink-0">
                                <Tags className="w-6 h-6 text-green-500" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-600">
                                  Discount Percentage
                                </p>
                                <p className="text-xs text-gray-500">
                                  Savings for your customers
                                </p>
                              </div>
                            </div>
                            <div className="w-full text-left sm:text-right sm:w-auto">
                              <span className="text-2xl font-bold text-green-500">
                                {calculateDiscount()}%
                              </span>
                              <p className="text-xs text-gray-500">OFF</p>
                            </div>
                          </div>
                          <div className="pt-3 mt-3 border-t border-green-100">
                            <div className="flex flex-col gap-2 justify-between sm:flex-row sm:gap-0">
                              <span className="text-sm text-gray-600">
                                Customer Savings:
                              </span>
                              <span className="text-sm font-medium text-green-600">
                                GHC {(originalPrice - discountPrice).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </div>
                  </motion.div>

                  {/* Location Details */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm sm:p-8"
                  >
                    <div className="flex flex-col gap-4 justify-between items-start mb-6 sm:flex-row sm:items-center">
                      <div className="flex gap-3 items-center">
                        <div className="p-2 bg-[#FF6C6C]/10 rounded-xl">
                          <Navigation className="w-6 h-6 text-[#FF6C6C]" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            Location Details
                          </h3>
                          <p className="text-sm text-gray-500">
                            Add up to 5 locations for your campaign
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2 items-start w-full sm:flex-row sm:items-center sm:gap-4 sm:w-auto">
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            setSelectAllLocations(!selectAllLocations);
                            if (!selectAllLocations) {
                              setRegionLocations([{ region: "all", location: "all" }]);
                              setFormData(prev => ({
                                ...prev,
                                region_location: [{ region: "all", location: "all" }]
                              }));
                            } else {
                              setRegionLocations([{ region: "", location: "" }]);
                              setFormData(prev => ({
                                ...prev,
                                region_location: [{ region: "", location: "" }]
                              }));
                            }
                          }}
                          className={`
                          flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl
                          transition-all duration-200 w-full sm:w-auto justify-center sm:justify-start
                          ${selectAllLocations
                            ? "bg-[#FF6C6C] text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }
                        `}
                        >
                          <div
                            className={`
                          w-4 h-4 border-2 rounded flex items-center justify-center
                          ${
                            selectAllLocations
                              ? "border-white"
                              : "border-gray-400"
                          }
                        `}
                          >
                            {selectAllLocations && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                          All Locations
                        </motion.button>

                        {!selectAllLocations && (
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={addRegionLocation}
                            disabled={regionLocations.length >= 5}
                            className={`
                            flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl
                            transition-all duration-200 w-full sm:w-auto justify-center sm:justify-start
                            ${
                              regionLocations.length >= 5
                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                : "bg-[#FF6C6C]/10 text-[#FF6C6C] hover:bg-[#FF6C6C]/20"
                            }
                          `}
                          >
                            <PlusCircle className="w-4 h-4" />
                            Add Location
                          </motion.button>
                        )}
                      </div>
                    </div>

                    <AnimatePresence mode="popLayout">
                      {!selectAllLocations &&
                        regionLocations.map((item, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, x: -100 }}
                            transition={{
                              type: "spring",
                              stiffness: 500,
                              damping: 30,
                            }}
                            className="relative p-4 sm:p-6 mb-4 bg-white rounded-xl border border-gray-100 
                          hover:border-[#FF6C6C]/30 hover:shadow-lg hover:shadow-[#FF6C6C]/5
                          transition-all duration-300 group"
                          >
                            <div className="grid grid-cols-1 gap-4 sm:gap-6">
                              {/* Region Select */}
                              <div className="relative">
                                <div
                                  className={`
                              relative rounded-xl border transition-all duration-200
                              ${
                                item.region
                                  ? "border-[#FF6C6C]/30"
                                  : "border-gray-200"
                              }
                              group-hover:border-[#FF6C6C]/50
                            `}
                                >
                                  <div className="absolute left-4 top-1/2 -translate-y-1/2">
                                    <Navigation
                                      className={`w-5 h-5 ${
                                        item.region
                                          ? "text-[#FF6C6C]"
                                          : "text-gray-400"
                                      }`}
                                    />
                                  </div>
                                  <select
                                    value={item.region}
                                    onChange={(e) =>
                                      handleRegionLocationChange(
                                        index,
                                        "region",
                                        e.target.value
                                      )
                                    }
                                    className="py-3 pr-10 pl-12 w-full text-sm bg-transparent rounded-xl transition-colors appearance-none cursor-pointer outline-none hover:bg-gray-50/50 sm:text-base"
                                  >
                                    <option value="">Select Region</option>
                                    {regionOptions.map((region) => (
                                      <option key={region} value={region}>
                                        {region}
                                      </option>
                                    ))}
                                  </select>
                                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                                    <ChevronDown className="w-4 h-4 text-gray-400" />
                                  </div>
                                </div>
                              </div>

                              {/* Location Select */}
                              <div className="relative">
                                <div
                                  className={`
                              relative rounded-xl border transition-all duration-200
                              ${
                                item.location
                                  ? "border-[#FF6C6C]/30"
                                  : "border-gray-200"
                              }
                              group-hover:border-[#FF6C6C]/50
                              ${!item.region ? "opacity-50" : ""}
                            `}
                                >
                                  <div className="absolute left-4 top-1/2 -translate-y-1/2">
                                    <MapPin
                                      className={`w-5 h-5 ${
                                        item.location
                                          ? "text-[#FF6C6C]"
                                          : "text-gray-400"
                                      }`}
                                    />
                                  </div>
                                  <select
                                    value={item.location}
                                    onChange={(e) =>
                                      handleRegionLocationChange(
                                        index,
                                        "location",
                                        e.target.value
                                      )
                                    }
                                    disabled={!item.region}
                                    className="py-3 pr-10 pl-12 w-full text-sm bg-transparent rounded-xl transition-colors appearance-none cursor-pointer outline-none hover:bg-gray-50/50 disabled:cursor-not-allowed sm:text-base"
                                  >
                                    <option value="">Select Location</option>
                                    {locationsByRegion[item.region]?.map(
                                      (location) => (
                                        <option key={location} value={location}>
                                          {location}
                                        </option>
                                      )
                                    )}
                                    <option value="other">Other</option>
                                  </select>
                                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                                    <ChevronDown className="w-4 h-4 text-gray-400" />
                                  </div>
                                </div>

                                {item.location === "other" && (
                                  <TextField
                                    value={item.customLocation || ""}
                                    onChange={(e) => handleCustomLocationChange(index, e.target.value)}
                                    placeholder="Enter custom location"
                                    fullWidth
                                    variant="outlined"
                                    sx={{
                                      marginTop: 2,
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "12px",
                                        "&:hover fieldset": {
                                          borderColor: "#FF6C6C",
                                        },
                                        "&.Mui-focused fieldset": {
                                          borderColor: "#FF6C6C",
                                        },
                                      },
                                    }}
                                  />
                                )}
                              </div>
                            </div>

                            {/* Remove Location Button */}
                            {regionLocations.length > 1 && (
                              <motion.button
                                initial={{ opacity: 0, scale: 0 }}
                                animate={{ opacity: 1, scale: 1 }}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => removeRegionLocation(index)}
                                className="absolute -top-3 -right-3 p-2 text-white bg-[#FF6C6C] rounded-full 
                              shadow-lg hover:bg-[#FF8F8F] transition-colors duration-200
                              opacity-0 group-hover:opacity-100 sm:opacity-0"
                              >
                                <Trash2 className="w-4 h-4" />
                              </motion.button>
                            )}

                            {/* Location Number Badge */}
                            <motion.div
                              className="absolute -left-3 top-1/2 -translate-y-1/2 flex items-center justify-center 
                            w-6 h-6 text-xs font-medium text-white bg-[#FF6C6C] rounded-full"
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ type: "spring", stiffness: 500 }}
                            >
                              {index + 1}
                            </motion.div>
                          </motion.div>
                        ))}
                    </AnimatePresence>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                    className="p-8 bg-white rounded-2xl border border-gray-100 shadow-sm"
                  >
                    <div className="flex gap-3 items-center mb-6">
                      <div className="p-3 bg-purple-50 rounded-xl">
                        <Calendar className="w-6 h-6 text-purple-500" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                          Campaign Duration
                        </h2>
                        <p className="text-sm text-gray-500">
                          Set the start and end dates
                        </p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div className="grid grid-cols-2 gap-6">
                        <TextField
                          label="Start Date"
                          type="date"
                          value={startDate}
                          onChange={(e) =>
                            handleDateChange("start_date", e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                          variant="outlined"
                          fullWidth
                          disabled
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                              "& fieldset": { borderColor: "#FF6C6C" },
                            },
                            "& .MuiInputLabel-root": { color: "#FF6C6C" },
                          }}
                        />

                        <TextField
                          label="End Date"
                          type="date"
                          value={endDate}
                          onChange={(e) =>
                            handleDateChange("expiry_date", e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                          inputProps={{
                            min: startDate,
                          }}
                          variant="outlined"
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "12px",
                              "&:hover fieldset": { borderColor: "#FF6C6C" },
                              "&.Mui-focused fieldset": {
                                borderColor: "#FF6C6C",
                              },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#FF6C6C",
                            },
                          }}
                        />
                      </div>

                      {calculateDays() !== null && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="p-4 mt-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100"
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex gap-3 items-center">
                              <div className="flex justify-center items-center w-12 h-12 rounded-xl bg-purple-500/10">
                                <Calendar className="w-6 h-6 text-purple-500" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-600">
                                  Campaign Duration
                                </p>
                                <p className="text-xs text-gray-500">
                                  Total days for your campaign
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className="text-2xl font-bold text-purple-500">
                                {calculateDays()}
                              </span>
                              <p className="text-xs text-gray-500">Days</p>
                            </div>
                          </div>
                          <div className="pt-3 mt-3 border-t border-purple-100">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">
                                Campaign Period:
                              </span>
                              <span className="font-medium text-purple-600">
                                {new Date(startDate).toLocaleDateString()} -{" "}
                                {new Date(endDate).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                    className="p-8 bg-white rounded-2xl border border-gray-100 shadow-sm"
                  >
                    <div className="flex gap-3 items-center mb-6">
                      <div className="p-3 bg-indigo-50 rounded-xl">
                        <MessageSquare className="w-6 h-6 text-indigo-500" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                          SMS Content
                        </h2>
                        <p className="text-sm text-gray-500">
                          Craft your SMS message
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <TextField
                        multiline
                        rows={4}
                        value={smsContent}
                        onChange={(e) => {
                          if (e.target.value.length <= MAX_CHARS) {
                            setSmsContent(e.target.value);
                          }
                        }}
                        placeholder="Enter your SMS content here..."
                        variant="outlined"
                        fullWidth
                        error={charCount === MAX_CHARS}
                        helperText={
                          charCount === MAX_CHARS
                            ? "Maximum character limit reached"
                            : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            "&:hover fieldset": { borderColor: "#FF6C6C" },
                            "&.Mui-focused fieldset": {
                              borderColor: "#FF6C6C",
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#FF6C6C",
                          },
                        }}
                      />

                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl border border-indigo-100"
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex gap-3 items-center">
                            <div className="flex justify-center items-center w-12 h-12 rounded-xl bg-indigo-500/10">
                              <Type className="w-6 h-6 text-indigo-500" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">
                                Character Count
                              </p>
                              <p className="text-xs text-gray-500">
                                Maximum {MAX_CHARS} characters
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="relative">
                              <svg className="w-16 h-16">
                                <circle
                                  className="text-gray-200"
                                  strokeWidth="5"
                                  stroke="currentColor"
                                  fill="transparent"
                                  r="26"
                                  cx="32"
                                  cy="32"
                                />
                                <circle
                                  className={`${
                                    charRemaining <= 10
                                      ? "text-red-500"
                                      : "text-indigo-500"
                                  }`}
                                  strokeWidth="5"
                                  strokeDasharray={164}
                                  strokeDashoffset={
                                    164 - (charCount / MAX_CHARS) * 164
                                  }
                                  strokeLinecap="round"
                                  stroke="currentColor"
                                  fill="transparent"
                                  r="26"
                                  cx="32"
                                  cy="32"
                                />
                              </svg>
                              <span
                                className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-lg font-bold ${
                                  charRemaining <= 10
                                    ? "text-red-500"
                                    : "text-indigo-500"
                                }`}
                              >
                                90
                              </span>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                              {charRemaining} remaining
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>

                  {/* Policy Details Section */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                    className="p-8 bg-white rounded-2xl border border-gray-100 shadow-sm"
                  >
                    <div className="flex gap-3 items-center mb-6">
                      <div className="p-3 bg-blue-50 rounded-xl">
                        <Type className="w-6 h-6 text-blue-500" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                          Policy Details
                        </h2>
                        <p className="text-sm text-gray-500">
                          Set your campaign policies and instructions
                        </p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      {[
                        {
                          id: "redemption_instructions",
                          label: "Redemption Instructions",
                          placeholder:
                            "Enter detailed instructions on how customers can redeem this deal...",
                          icon: <Target className="w-6 h-6 text-blue-500" />,
                        },
                        {
                          id: "refund_policy",
                          label: "Refund Policy",
                          placeholder:
                            "Specify your refund policy for this deal...",
                          icon: <RefreshCw className="w-6 h-6 text-blue-500" />,
                        },
                        {
                          id: "terms_and_conditions",
                          label: "Terms and Conditions",
                          placeholder:
                            "Enter the terms and conditions for this deal...",
                          icon: (
                            <AlertCircle className="w-6 h-6 text-blue-500" />
                          ),
                        },
                      ].map((field) => (
                        <div key={field.id} className="space-y-2">
                          <TextField
                            label={field.label}
                            multiline
                            rows={4}
                            variant="outlined"
                            fullWidth
                            placeholder={field.placeholder}
                            inputProps={{ maxLength: MAX_POLICY_CHARS }}
                            value={formData[field.id] || ""}
                            onChange={(e) => {
                              handleTextFieldChange(field.id, e.target.value);
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "12px",
                                "&:hover fieldset": { borderColor: "#FF6C6C" },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#FF6C6C",
                                },
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#FF6C6C",
                              },
                            }}
                          />

                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm"
                          >
                            <div className="flex justify-between items-center">
                              <div className="flex gap-3 items-center">
                                <div className="p-2 bg-gray-50 rounded-lg">
                                  {field.icon}
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium text-gray-700">
                                    {charCounts[field.id]} / {MAX_POLICY_CHARS}
                                  </span>
                                  <div className="flex gap-2 items-center">
                                    <div className="overflow-hidden relative w-32 h-1.5 bg-gray-100 rounded-full">
                                      <motion.div
                                        initial={{ width: 0 }}
                                        animate={{
                                          width: `${
                                            (charCounts[field.id] /
                                              MAX_POLICY_CHARS) *
                                            100
                                          }%`,
                                          backgroundColor:
                                            charCounts[field.id] >=
                                            MAX_POLICY_CHARS - 20
                                              ? ["#FF6C6C", "#ef4444"]
                                              : ["#3b82f6", "#60a5fa"],
                                        }}
                                        transition={{
                                          backgroundColor: {
                                            duration: 0.3,
                                            repeat:
                                              charCounts[field.id] >=
                                              MAX_POLICY_CHARS - 20
                                                ? Infinity
                                                : 0,
                                            repeatType: "reverse",
                                          },
                                        }}
                                        className="absolute top-0 left-0 h-full rounded-full"
                                      />
                                    </div>
                                    <motion.span
                                      className={`text-xs ${
                                        charCounts[field.id] >=
                                        MAX_POLICY_CHARS - 20
                                          ? "text-red-500"
                                          : "text-blue-500"
                                      }`}
                                      animate={{
                                        opacity:
                                          charCounts[field.id] >=
                                          MAX_POLICY_CHARS - 20
                                            ? [1, 0.5, 1]
                                            : 1,
                                      }}
                                      transition={{
                                        duration: 1,
                                        repeat: Infinity,
                                        repeatType: "reverse",
                                      }}
                                    >
                                      {MAX_POLICY_CHARS - charCounts[field.id]}{" "}
                                      remaining
                                    </motion.span>
                                  </div>
                                </div>
                              </div>

                              {charCounts[field.id] >=
                                MAX_POLICY_CHARS - 20 && (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  className="flex gap-2 items-center px-3 py-1.5 bg-red-50 rounded-lg"
                                >
                                  <AlertOctagon className="w-4 h-4 text-red-500" />
                                  <span className="text-xs font-medium text-red-600">
                                    {charCounts[field.id] >= MAX_POLICY_CHARS
                                      ? "Limit reached"
                                      : "Approaching limit"}
                                  </span>
                                </motion.div>
                              )}
                            </div>
                          </motion.div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                </div>

                {/* Scroll Indicator */}
                {showScrollIndicator && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="hidden absolute bottom-0 left-1/2 pb-4 text-center transform -translate-x-1/2 lg:block"
                  >
                    <div className="flex flex-col items-center space-y-2">
                      <motion.div
                        animate={{ y: [0, 5, 0] }}
                        transition={{ repeat: Infinity, duration: 1.5 }}
                        className="w-1 h-6 bg-gradient-to-b from-[#FF6C6C] to-transparent rounded-full"
                      />
                      <span className="text-sm text-gray-500">
                        Scroll for more
                      </span>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Right Column - Static Preview & Stats */}
              <div className="space-y-6 lg:sticky lg:top-8">
                {/* Campaign Preview */}
                <div className="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm sm:p-6">
                  <h3 className="mb-4 text-lg font-semibold text-gray-900">
                    Campaign Preview
                  </h3>
                  <div className="overflow-hidden mb-4 bg-gray-100 rounded-xl aspect-video">
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="object-cover w-full h-full"
                      />
                    ) : (
                      <div className="flex justify-center items-center w-full h-full">
                        <ImagePlus className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    style={{
                      borderColor: "#FF6C6C",
                      color: "#FF6C6C",
                      borderRadius: "12px",
                      padding: "10px",
                    }}
                  >
                    Upload Image
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </Button>
                </div>

                {/* Quick Stats and Actions */}
                <div className="space-y-4">
                  <div className="bg-gradient-to-br from-[#FF6C6C] to-[#FF8F8F] rounded-2xl p-4 sm:p-6 text-white">
                    <h3 className="mb-4 text-lg font-semibold">
                      Campaign Reach
                    </h3>
                    <div className="flex justify-between items-center">
                      <span>Estimated Reach</span>
                      <span className="font-semibold">5,000 - 10,000</span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="sticky bottom-0 p-4 space-y-3 bg-white shadow-lg lg:p-0 lg:bg-transparent lg:shadow-none">
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      style={{
                        backgroundColor: "#FF6C6C",
                        borderRadius: "12px",
                        padding: "12px 24px",
                      }}
                    >
                      {isSubmitting ? (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="flex gap-2 items-center"
                        >
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{
                              duration: 1,
                              repeat: Infinity,
                              ease: "linear",
                            }}
                          >
                            <RefreshCw className="w-5 h-5" />
                          </motion.div>
                          <span>
                            {slug ? "Updating..." : "Creating..."}
                          </span>
                        </motion.div>
                      ) : (
                        `${slug ? "Update" : "Create"} Campaign Deal`
                      )}
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => navigate(-1)}
                      disabled={isSubmitting}
                      style={{
                        borderColor: "#FF6C6C",
                        color: "#FF6C6C",
                        borderRadius: "12px",
                        padding: "12px 24px",
                        backgroundColor: "white",
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateCampaignDeal;
